// Published insight articles. Add new articles here — they appear automatically
// on the Insights page and at /insights/<slug>.

export const ARTICLES = [
  {
    slug: "iso-27001-gap-assessment",
    title: "Preparing for an ISO 27001 Gap Assessment",
    category: "ISO Standards",
    excerpt:
      "What a gap assessment examines, how to prepare documentation and stakeholders, and how to get the most value from the exercise.",
    date: "14 July 2026",
    readTime: "6 min read",
    intro:
      "A gap assessment is often the first structured step toward ISO 27001 certification — and one of the most valuable. Done well, it gives leadership an honest picture of where the organization stands and a prioritized path forward. Done casually, it produces a report that sits unread. Preparation makes the difference.",
    sections: [
      {
        heading: "What a gap assessment actually examines",
        body: [
          "A gap assessment compares your current information-security practices against the requirements of the standard: the clauses covering context, leadership, planning, support, operation, performance evaluation and improvement, together with the controls relevant to your environment. Assessors look at documentation, but also at how work is genuinely performed — interviews, records and observation matter as much as policies.",
          "The output is not a pass or fail. It is a structured map: what conforms, what partially conforms, what is missing, and how significant each gap is to your risk and certification objectives.",
        ],
      },
      {
        heading: "Define the scope before anyone assesses anything",
        body: [
          "The most common preparation failure is an undefined or unrealistic scope. Before the assessment, agree on which business units, systems, locations and information assets the management system will cover. Scope decisions made late — or changed mid-assessment — waste effort and blur findings.",
          "A well-defined scope also keeps the assessment proportionate. A payment platform and a marketing microsite do not need the same depth of scrutiny.",
        ],
      },
      {
        heading: "Get documentation honest, not perfect",
        body: [
          "Organizations sometimes rush to write impressive policies in the weeks before an assessment. This is counterproductive. Assessors are not grading the polish of your documents — they are testing whether documented practice matches actual practice. A modest policy that people follow is worth more than an elegant one nobody has read.",
          "Instead, gather what genuinely exists: current policies, procedures, records of decisions, access reviews, incident logs, training records. Where documents are missing, note it. The assessment exists to find exactly that.",
        ],
      },
      {
        heading: "Prepare your people",
        body: [
          "Interviews are central to a credible assessment. Brief the people who will be involved — not with scripted answers, but with an understanding of what the assessment is for and encouragement to describe how work really happens. Findings based on reality are findings you can act on.",
          "It also helps to nominate one internal coordinator: someone who can locate records, schedule interviews and answer scope questions quickly.",
        ],
      },
      {
        heading: "Treat the findings as a roadmap, not a verdict",
        body: [
          "The value of a gap assessment is realized in what follows it. Expect a prioritized set of findings — and insist on prioritization by risk and effort, not a flat list. Sequence remediation into phases: quick corrections, medium-term control build-out, and structural improvements such as risk-assessment methodology and management review.",
          "Organizations that treat the gap assessment as the start of a management system — rather than a hurdle before an audit — consistently reach certification readiness faster and with a system that actually works.",
        ],
      },
    ],
  },
  {
    slug: "bc-operational-readiness",
    title: "Business Continuity: From Documentation to Operational Readiness",
    category: "Business Continuity",
    excerpt:
      "Why continuity plans fail when they live only on paper — and how exercising and testing turn documentation into real capability.",
    date: "14 July 2026",
    readTime: "7 min read",
    intro:
      "Most organizations that experience a serious disruption do have a business continuity plan. Many discover, in the middle of the incident, that having a plan and being able to execute it are different things. Operational readiness — not documentation — is what determines how an organization absorbs disruption.",
    sections: [
      {
        heading: "Why plans fail on paper",
        body: [
          "Continuity documents usually fail in predictable ways: contact lists are outdated, recovery procedures assume systems or suppliers that have changed, roles are assigned to people who have never rehearsed them, and recovery time objectives were set without testing whether they are achievable.",
          "None of these failures are visible in a document review. They only surface when the plan is exercised — or, worse, when it is invoked for real.",
        ],
      },
      {
        heading: "Start with the business impact analysis",
        body: [
          "Readiness begins with knowing what must survive. A business impact analysis identifies the activities whose disruption would hurt most, how quickly, and what those activities depend on — people, systems, data, facilities and third parties. Continuity strategies built on this analysis protect what matters; strategies built on assumption protect what is easiest to document.",
          "Keep the analysis current. Organizations change faster than most continuity documentation does.",
        ],
      },
      {
        heading: "Design for decisions, not documents",
        body: [
          "In a real disruption, people need clarity about who decides what, how information flows, and in what order activities are restored. Plans should therefore be built around decision-making: escalation paths, crisis roles, communication protocols and concise action guides.",
          "A useful test: could a competent manager who has never read the plan pick it up during an incident and know what to do within five minutes? If not, simplify.",
        ],
      },
      {
        heading: "Exercise realistically",
        body: [
          "Exercising is where documentation becomes capability. Tabletop exercises test decision-making; technical tests validate recovery procedures; communication exercises confirm that contact trees and stakeholder messaging work under pressure.",
          "Start modestly and increase realism over time. Record what happened, assign corrective actions and — critically — verify they are completed before the next exercise. The ISO 22301 continual-improvement cycle exists precisely for this.",
        ],
      },
      {
        heading: "Measure readiness, not compliance",
        body: [
          "Track indicators that reflect actual capability: percentage of critical activities with tested recovery procedures, time since the last exercise per business unit, corrective actions closed, and contact-list verification rates. These measures tell leadership whether readiness is improving — something a plan's page count never will.",
        ],
      },
      {
        heading: "From plan to capability",
        body: [
          "The shift from documentation to readiness is cultural as much as procedural. Organizations that recover well are those where continuity is exercised, measured and owned by the business — not archived by a single department. That shift is exactly what a well-implemented business continuity management system is designed to produce.",
        ],
      },
    ],
  },
  {
    slug: "vapt-remediation-plan",
    title: "Turning Vulnerability Findings into an Effective Remediation Plan",
    category: "Cybersecurity",
    excerpt:
      "A practical approach to prioritizing findings, assigning ownership and verifying that fixes actually close the risk.",
    date: "14 July 2026",
    readTime: "6 min read",
    intro:
      "A penetration test or vulnerability assessment earns its value only when its findings are fixed. Yet in many organizations, reports arrive, severity ratings are noted, and months later the next assessment rediscovers the same weaknesses. The difference between organizations that improve and those that repeat is a disciplined remediation process.",
    sections: [
      {
        heading: "The gap between findings and fixes",
        body: [
          "Remediation stalls for understandable reasons: findings compete with delivery work, reports mix critical issues with informational noise, ownership is ambiguous, and nobody validates that a deployed fix actually resolves the underlying weakness.",
          "Each of these is a process problem, and each has a practical answer. The starting point is to treat remediation as a managed workstream — with ownership, sequencing and verification — rather than as an informal request to the technical team.",
        ],
      },
      {
        heading: "Prioritize by risk, not severity alone",
        body: [
          "Severity scores are a starting point, not a plan. A medium-severity finding on an internet-facing payment system may deserve attention before a high-severity finding on an isolated test server. Effective prioritization weighs exploitability, exposure, the sensitivity of affected data and systems, and the effort required to fix.",
          "Group related findings as well. Several moderate weaknesses sharing one root cause — such as missing patch governance — are often best closed by a single corrective action.",
        ],
      },
      {
        heading: "Assign ownership and realistic deadlines",
        body: [
          "Every finding needs a named owner and a target date that reflects risk: genuinely critical, exploitable issues in days; systemic improvements over a planned period. Deadlines set without reference to team capacity are simply missed on schedule — agree them with the people doing the work.",
          "Track status visibly. A simple register showing findings, owners, due dates and verification status creates the accountability that report appendices never do.",
        ],
      },
      {
        heading: "Validate every fix",
        body: [
          "Closed should mean verified. Retest critical and high-risk fixes against the original evidence, and confirm the resolution does not introduce new weaknesses. Where a finding is accepted rather than fixed — a legitimate outcome for low-risk items — record the decision, the rationale and the accepting authority.",
          "Validation evidence also answers the question auditors, clients and regulators increasingly ask: not just 'were you tested?' but 'what did you do about it?'",
        ],
      },
      {
        heading: "Address the root causes",
        body: [
          "Repeat findings across successive assessments signal process weaknesses: change management that bypasses security review, hardening standards that are not enforced, or development practices without security checkpoints. Fixing the instance without fixing the process guarantees the finding's return.",
          "The most valuable outcome of remediation is often a control improvement — a patch cadence, a configuration baseline, a pre-release security check — that prevents entire classes of future findings.",
        ],
      },
      {
        heading: "Report progress in business terms",
        body: [
          "Leadership sustains remediation when progress is visible in their language: risk reduced, exposure windows closed, client and audit commitments met. A short, regular summary — open findings by risk, ageing, and closure rate — keeps security improvement on the agenda between assessments.",
        ],
      },
    ],
  },
];
